/* eslint-disable prefer-promise-reject-errors */
/**
 * Base implementation of low-level transport. Each low-level transport
 * should implement following methods:
 * <ul>
 *     <li>send(endpoint : String, headers: {}, method : String, params : *[]) :
 *          Promise<Object> - performs a network query. </li>
 * </ul>
 */
import _ from 'lodash';
import { v4 as generateGuid } from 'uuid';
import cookie from 'js-cookie';
import { events } from 'gw-platform-events-js';
import { trackException, trackEvent, getMethodNameFromBody} from '../../../applications/account-management/src/wsib/common/application-insight/application-insight';

const BACKEND_ACTIVITY_EVENT = 'backend::requestsent';

// commenting the code as part of iOS issue related to JSESSION ID
// this code was generating new JSESSION ID which was not in the correct format

// // function manageStickySessionCookie() {
//     const STICKY_SESSION_ID = 'JSESSIONID';
//     if (!cookie.get(STICKY_SESSION_ID)) {
//         cookie.set(STICKY_SESSION_ID, generateGuid());
//     }
// }

export const JSON_CONTENT_TYPE = 'application/json';

export default {

    send: (endpoint, headers, body) => {
        const baseHeaders = {
            Accept: JSON_CONTENT_TYPE,
            'Accept-Language': localStorage.getItem('selectedLanguage')
        };
        // manageStickySessionCookie();
        const newHeaders = Object.assign(baseHeaders, headers);

        const requestParams = {
            method: 'POST',
            headers: newHeaders,
            body: body
        };
        events.emit(BACKEND_ACTIVITY_EVENT, {
            endpoint,
            ...requestParams
        });

        let skipExceptionLog = false;

        return fetch(endpoint, requestParams).then((res) => {
            const responseContentType = res.headers.get('Content-Type');
            if (!res.ok) {
                // status NOT in the range 200-299
                if (res.status === 401) {
                    skipExceptionLog = true;
                    // in case text() failed, just catch the excepion and log without response body
                    res.text().then((data) => {
                        const ret = {
                            endpoint: endpoint,
                            status: res?.status,
                            status_text: res?.statusText,
                            request_headers: headers,
                            request_body: body,
                            response_header: res?.headers,
                            response_body: data
                        };
                        const method = getMethodNameFromBody(body);
                        trackException(`fetch 401 error: ${res.status} - ${endpoint} - ${method}`, ret);
                    }).catch((e) => {
                        const ret = {
                            endpoint: endpoint,
                            status: res?.status,
                            status_text: res?.statusText,
                            request_headers: headers,
                            request_body: body,
                            response_header: res?.headers,
                        };
                        const method = getMethodNameFromBody(body);
                        trackException(`fetch 401 error no response: ${res.status} - ${endpoint} - ${method}`, ret, e);
                    });
                    return Promise.reject({
                        status: 401
                    });
                }
                if (responseContentType && responseContentType.includes(JSON_CONTENT_TYPE)) {
                    skipExceptionLog = true;
                    return res.json().then((data) => {
                        const ret = {
                            endpoint: endpoint,
                            status: res?.status,
                            status_text: res?.statusText,
                            request_headers: headers,
                            request_body: body,
                            response_header: res?.headers,
                            response_body: data
                        };
                        const method = getMethodNameFromBody(body);
                        trackException(`fetch error json: ${res.status} - ${endpoint} - ${method}`, ret);
                        return Promise.reject(_.pick(data, 'id', 'error'));
                    });
                }
                // reject with Error if response does not contain parsable JSON content
                // in case text() failed, just catch the excepion and log without response body
                skipExceptionLog = true;
                res.text().then((txt) => {
                    const ret = {
                        endpoint: endpoint,
                        status: res?.status,
                        status_text: res?.statusText,
                        request_headers: headers,
                        request_body: body,
                        response_header: res?.headers,
                        response_body: txt
                    };
                    const method = getMethodNameFromBody(body);
                    trackException(`fetch error text: ${res.status} - ${endpoint} - ${method}`, ret);
                }).catch((e) => {
                    const ret = {
                        endpoint: endpoint,
                        status: res?.status,
                        status_text: res?.statusText,
                        request_headers: headers,
                        request_body: body,
                        response_header: res?.headers
                    };
                    const method = getMethodNameFromBody(body);
                    trackException(`fetch error no response: ${res.status} - ${endpoint} - ${method}`, ret, e);
                });
                return Promise.reject(new Error(`${res.status} (${res.statusText})`));
            }
            return res.json();
        }).then((data) => {
            return _.pick(data, 'id', 'result');
        }).catch((err) => {
            // log the error as exception with requst and respond
            if (!skipExceptionLog) {
                const ret = {
                    endpoint: endpoint,
                    request_headers: headers,
                    request_body: body
                };
                const method = getMethodNameFromBody(body);
                trackException(`fetch exception: ${endpoint} - ${method}`, ret, err);
            }
            throw err;
        });
    }
};
